/* istanbul ignore file */
/* eslint object-shorthand: "off"*/
import { useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import { getColor } from 'common/constants/_colors';
import { renderToString } from 'react-dom/server';
import { COLORS_PALLETE_BY_RESOURCE_TYPE } from '../../../AllocationOverTime/Graph/graph.palletes';
import { RESOURCE_TYPES } from '../../../resource_types';
import useRaCostSetting from '../../../settings/hooks/useRaCostSetting';
import Tooltip from '../../../AllocationOverTime/Graph/tooltips/Tooltip';
import ResourceAllocationContext from '../../../state/context';
import { ResourceAllocationFiltersContext } from '../../../../state/filters/contexts';
import { CHART_LEGEND_ITEM_STYLE } from 'components/base/common-styles';
import { generateResourceColorKey } from '../../../state/hooks/useRaColorAssigner';
import { GRAPH_OTHERS_KEY } from '../../../AllocationOverTime/Graph/useGraph';
import { getRoundedFte } from '../../../resource-allocation.formatters';

const DEFAULT_PALLETE =
  COLORS_PALLETE_BY_RESOURCE_TYPE[RESOURCE_TYPES.JIRA_PROJECTS.key];

const useGraph = ({
  data = [],
  resourceType = RESOURCE_TYPES.JIRA_ISSUE_TYPES
}) => {
  const { colorAssigner } = useContext(ResourceAllocationContext);
  const {
    state: { period, isInitialized }
  } = useContext(ResourceAllocationFiltersContext);
  const raCost = useRaCostSetting();
  const [graphConfig, setGraphConfig] = useState({});
  const colorPallete =
    COLORS_PALLETE_BY_RESOURCE_TYPE[resourceType.key] ||
    COLORS_PALLETE_BY_RESOURCE_TYPE[resourceType.type] ||
    DEFAULT_PALLETE;

  const initGraphConfig = () => {
    colorAssigner.switchPalette(colorPallete);
    const series = {
      name: resourceType.name,
      colorByPoint: true,
      data: data.map((item, idx) => {
        const colKey = _.get(item, 'key') || _.get(item, 'name') || '';
        const data = _.get(item, 'allocation_data[0]');
        const percent = _.get(data, 'effort_p');

        const colorKey = generateResourceColorKey(resourceType.key, colKey);
        const color = colorAssigner.assignColor(colorKey);

        const isOthers = colKey.toLowerCase() === GRAPH_OTHERS_KEY;

        return {
          name: colKey,
          y: percent,
          extraData: {
            ...data,
            start_period: period.after,
            end_period: period.before
          },
          isOthers,
          color,
          visible: !isOthers
        };
      })
    };

    const newGraphConfig = {
      chart: {
        type: 'pie',
        style: {
          fontFamily: 'Outfit'
        }
      },
      series,
      title: {
        text: null
      },
      subtitle: {
        text: null
      },
      legend: {
        layout: 'vertical',
        floating: false,
        verticalAlign: 'middle',
        align: 'right',
        x: 0,
        itemMarginBottom: 10,
        labelFormatter: function () {
          return `${this.name} (${getRoundedFte(this.y, 1)}%)`;
        },
        itemStyle: CHART_LEGEND_ITEM_STYLE
      },
      plotOptions: {
        pie: {
          size: '80%',
          cursor: 'pointer',
          dataLabels: {
            enabled: false
          },
          showInLegend: true,
          innerSize: '60%'
        },
        series: {
          cursor: 'pointer',
          point: {
            // tooltip onClick instead of onHover
            events: {
              click: function () {
                this.series.chart.update({
                  tooltip: {
                    enabled: true
                  }
                });
              },
              mouseOut: function () {
                this.series.chart.update({
                  tooltip: {
                    enabled: false
                  }
                });
              }
            }
          }
        }
      },
      tooltip: {
        useHTML: true,
        backgroundColor: '#FFF',
        stickOnContact: false,
        borderColor: getColor('botticelli'),
        enabled: false,
        outside: true,
        formatter: /* istanbul ignore next */ function () {
          return renderToString(
            <Tooltip event={this} resourceType={resourceType} raCost={raCost} />
          );
        },
        style: {
          zIndex: 100,
          width: 'auto'
        }
      },
      credits: {
        enabled: false
      }
    };

    setGraphConfig(newGraphConfig);
  };

  useEffect(() => {
    if (!isInitialized) return;
    initGraphConfig();
  }, [
    isInitialized,
    data,
    resourceType,
    period,
    raCost.isEnabled,
    raCost.value,
    raCost.currency
  ]);

  return graphConfig;
};

export default useGraph;
